import { useMemo, useState } from 'react';
import GOAL_EDIT_MODAL from './goal-edit-modal';
import { goalProgress } from './goal-form';

export default function GOAL_LIST({ deleteThisGoal, getUserGoals, userGoals = [], allGoals = userGoals, moveGoal, reordering, canReorder = true }) {
  const [editingGoal, setEditingGoal] = useState(null);
  const [expanded, setExpanded] = useState({});
  const goalsById = useMemo(() => new Map(allGoals.map(goal => [goal._id, goal])), [allGoals]);
  const { roots, children } = useMemo(() => {
    const ids = new Set(userGoals.map(goal => goal._id));
    const children = {};
    const roots = [];
    userGoals.forEach(goal => {
      if (goal.assigned_parent_ID && ids.has(goal.assigned_parent_ID)) {
        (children[goal.assigned_parent_ID] ||= []).push(goal);
      } else roots.push(goal); // A search matching only a subgoal still shows its result.
    });
    return { roots, children };
  }, [userGoals]);

  const deadline = goal => {
    const visited = new Set();
    let item = goal;
    while (item && !visited.has(item._id)) {
      visited.add(item._id);
      const date = item.quarterEndDate || item.assigned_year_ID?.endDate;
      if (date) return new Date(date).toLocaleDateString('en-GB');
      item = goalsById.get(item.assigned_parent_ID);
    }
    return '';
  };

  const row = (goal, ancestors = []) => {
    if (ancestors.includes(goal._id)) return null;
    const siblings = children[goal.assigned_parent_ID] || [];
    const index = siblings.findIndex(item => item._id === goal._id);
    const isSubgoal = !!goal.assigned_parent_ID;
    const type = isSubgoal ? 'sub' : goal.quarterNumber ? 'quarterly' : goal.assigned_year_ID ? 'yearly' : '';
    const label = goal.status === 'completed' ? 'Complete' : goal.status === 'in-progress' ? 'In Progress' : 'Not Started';
    return <div key={goal._id} className={`goal-list__item ${isSubgoal ? 'goal-list__item--subgoal' : ''}`}>
      <div className="goal-list__item-content">
        <div className="goal-list__name-col">
          {children[goal._id]?.length > 0 && <button className={`goal-list__expand-btn ${expanded[goal._id] ? 'expanded' : ''}`} aria-label={`Show subgoals for ${goal.name}`} aria-expanded={!!expanded[goal._id]} onClick={() => setExpanded(previous => ({ ...previous, [goal._id]: !previous[goal._id] }))}>▶</button>}
          <span>{goal.name}</span>
          {type && <span className={`goal-type-pill goal-type-pill--${type}`}>{type === 'sub' ? 'Subgoal' : type}</span>}
        </div>
        <div className="goal-list__dates-col">{deadline(goal)}</div>
        <div className="goal-list__status-col"><span className={`btn-status status-${goal.status}`}>{label} {goalProgress(goal)}%</span></div>
      </div>
      <div className="goal-list__actions-col">
        {isSubgoal && moveGoal && canReorder && <>
          <button aria-label={`Move ${goal.name} up`} title="Move subgoal up" disabled={reordering || index <= 0} onClick={() => moveGoal(goal._id, 'up')}>↑</button>
          <button aria-label={`Move ${goal.name} down`} title="Move subgoal down" disabled={reordering || index < 0 || index === siblings.length - 1} onClick={() => moveGoal(goal._id, 'down')}>↓</button>
        </>}
        <button onClick={() => setEditingGoal(goal)}>Edit</button>
        {deleteThisGoal && <button onClick={() => deleteThisGoal(goal._id)}>Delete</button>}
      </div>
      {expanded[goal._id] && children[goal._id]?.length > 0 && <div className="goal-list__children">{children[goal._id].map(child => row(child, [...ancestors, goal._id]))}</div>}
    </div>;
  };

  return <>
    <div className="goal-list-header"><span>Goal</span><span>Deadline</span><span>Status</span></div>
    <div id="goal-list">{roots.length ? roots.map(goal => row(goal)) : <p>No goals found.</p>}</div>
    <GOAL_EDIT_MODAL isOpen={!!editingGoal} onClose={() => setEditingGoal(null)} goal={editingGoal} getUserGoals={getUserGoals} allGoals={allGoals} />
  </>;
}
